import { createContext, type ReactElement, type ReactNode, useContext, useMemo } from "react"

/**
 * A demo affordance for proposal 03's central claim: that one endpoint can front
 * both halves of the Digital Library without the client being able to tell.
 *
 * That is exactly what makes it hard to review. With the facade running, the page
 * looks like one API, so nobody can see which fields would need media-backend
 * work and which already exist on `/seats` today. This toggle draws the seam back
 * on: every rendered element is outlined by the system it actually came from.
 *
 * POC only, and deliberately client-side. Provenance is not a fact the facade
 * reports — it is a fact about which hook fetched the value, which the client
 * already knows. `useLibraryQuery` hits `libraryV2` on the DCE graph;
 * `useEntitlements` hits `user.entitlements`, which only the facade serves and
 * which is really `mlc.uat.lifeway.com/api-my/seats`. Nothing here needs a schema
 * change to be true.
 */
export type DataSource = "dce" | "mlc"

export interface ProvenanceState {
  readonly isOn: boolean
}

const ProvenanceContext = createContext<ProvenanceState>({ isOn: false })

interface ProvenanceProviderProps {
  readonly isOn: boolean
  readonly children: ReactNode
}

export const ProvenanceProvider = ({ isOn, children }: ProvenanceProviderProps): ReactElement => {
  const value = useMemo(() => ({ isOn }), [isOn])

  return <ProvenanceContext.Provider value={value}>{children}</ProvenanceContext.Provider>
}

export const useProvenance = (): ProvenanceState => useContext(ProvenanceContext)
